// src/services/groupService.ts
// Domain-specific service for group operations

import { apiClient } from "./api";
import { API_ENDPOINTS } from "../constants/index";
import {
  normalizeGroup,
  normalizeTile,
  type NormalizedGroup,
  type NormalizedTile,
  type RawGroup,
  type RawTile,
} from "../utils/dataUtils";
import type { Note } from "../types/entities";
import { cache, CACHE_KEYS, withCache } from "../utils/cacheUtils";
import { logger } from "@common/utils/logger";

// Type definitions
export type { RawGroup };

/** `GroupRead`: a group as /groups returns it. */
export interface GroupRead {
  group_id: number;
  group_name: string;
  group_description?: string | null;
  group_type: string;
  view: string;
  notes?: Note[];
  tiles?: RawTile[];
  owner_email: string;
  group_expanded?: boolean;
}

/** A /groups group with its tiles and notes, the aliases filled in. */
export interface GroupWithDetails {
  id?: number;
  group_id: number;
  group_name: string;
  group_type: string;
  group_expanded: boolean;
  owner_email: string;
  view: string;
  tiles: NormalizedTile[];
  notes: Note[];
}

/** `GroupBase` (POST /groups), less the owner the service adds. */
export interface CreateGroupData {
  group_name: string;
  group_type?: string;
  group_description?: string | null;
  group_expanded?: boolean;
  view?: string;
  description?: string;
  workspace_id?: number;
}

/** `GroupUpdate` (PUT /groups/{id}): only the fields to change. */
export interface UpdateGroupData {
  group_name?: string;
  group_type?: string;
  group_description?: string | null;
  group_expanded?: boolean;
  view?: string;
  description?: string;
}

export interface GroupWorkspaceLink {
  workspaceId: number;
  groupId: number;
}

/**
 * Drop every cache that embeds groups: the group lists and the workspaces
 * (which carry their groups inline). Any request for them already in flight
 * is superseded, so it can't write the pre-change data back.
 */
export function invalidateGroupCaches(): void {
  cache.remove(CACHE_KEYS.GROUPS);
  cache.remove(CACHE_KEYS.GROUPS + "_detailed");
  cache.remove(CACHE_KEYS.WORKSPACES);
  cache.remove("workspaces-with-groups");
  cache.remove("workspaces-with-details");
  cache.removeByPrefix("workspace-details-");
}

/**
 * Service for group operations
 */
export class GroupService {
  /**
   * Get all groups (with caching)
   */
  async getGroups(
    onFreshData?: (data: NormalizedGroup[]) => void,
  ): Promise<NormalizedGroup[]> {
    return withCache(
      CACHE_KEYS.GROUPS,
      async () => {
        const groups = await apiClient.get<RawGroup[]>(API_ENDPOINTS.GROUPS);
        const groupArray = Array.isArray(groups) ? groups : [];
        return groupArray.map(normalizeGroup);
      },
      {
        ttl: 5 * 60 * 1000, // 5 minutes cache
        onCacheHit: () => logger.debug("[GroupService] Using cached groups"),
        onCacheMiss: () => logger.debug("[GroupService] Fetching fresh groups"),
        onFreshData,
      },
    );
  }

  /**
   * Get all groups with detailed tiles and notes (with caching)
   */
  async getGroupsWithDetails(options?: {
    forceRefresh?: boolean;
    onFreshData?: (data: GroupWithDetails[]) => void;
  }): Promise<GroupWithDetails[]> {
    const { forceRefresh, onFreshData } = options || {};

    return withCache(
      CACHE_KEYS.GROUPS + "_detailed",
      async () => {
        // The /groups/ endpoint already returns all groups with tiles and notes eagerly loaded
        // No need for N+1 queries - just use the response directly
        const response = await apiClient.get<GroupRead[]>(API_ENDPOINTS.GROUPS);
        const groups = Array.isArray(response) ? response : [];

        const groupsWithDetails = groups.map((g): GroupWithDetails => {
          // Normalize tiles to ensure consistent ID field (tile_id -> id)
          // This prevents duplicates when the same link has different key fields
          const rawTiles = g.tiles || [];
          const tiles = rawTiles.map(normalizeTile);

          return {
            group_id: g.group_id,
            group_name: g.group_name,
            group_type: g.group_type,
            group_expanded: g.group_expanded || false,
            owner_email: g.owner_email,
            view: g.view,
            tiles: tiles,
            notes: g.notes || [],
          };
        });

        return groupsWithDetails;
      },
      {
        ttl: 5 * 60 * 1000, // 5 minutes cache
        forceRefresh,
        onCacheHit: () =>
          logger.debug("[GroupService] Using cached detailed groups"),
        onCacheMiss: () =>
          logger.debug("[GroupService] Fetching fresh detailed groups"),
        onFreshData,
      },
    );
  }

  /**
   * Get a specific group with its content
   */
  async getGroup(groupId: number): Promise<NormalizedGroup> {
    const group = await apiClient.get<RawGroup>(
      `${API_ENDPOINTS.GROUPS}${groupId}`,
    );
    return normalizeGroup(group);
  }

  /**
   * Create a new group
   */
  async createGroup(
    groupData: CreateGroupData,
    userEmail: string,
  ): Promise<NormalizedGroup> {
    const processedData = {
      ...groupData,
      owner_email: userEmail,
    };

    const newGroup = await apiClient.post<RawGroup>(
      API_ENDPOINTS.GROUPS,
      processedData,
    );

    // Invalidate cache after creating
    invalidateGroupCaches();

    return normalizeGroup(newGroup);
  }

  /**
   * Update an existing group
   */
  async updateGroup(
    groupId: number,
    groupData: UpdateGroupData,
  ): Promise<NormalizedGroup> {
    const updatedGroup = await apiClient.put<RawGroup>(
      `${API_ENDPOINTS.GROUPS}${groupId}`,
      groupData,
    );

    // Workspaces carry their groups inline, so their caches hold the old
    // group too.
    invalidateGroupCaches();

    return normalizeGroup(updatedGroup);
  }

  /**
   * Delete a group
   */
  async deleteGroup(groupId: number): Promise<number> {
    await apiClient.delete(`${API_ENDPOINTS.GROUPS}${groupId}`);

    // Invalidate cache after deleting
    invalidateGroupCaches();

    return groupId;
  }

  /**
   * Reorder items within a group
   */
  async reorderGroupItems(
    groupId: number,
    itemOrder: number[],
  ): Promise<NormalizedGroup> {
    const reorderedGroup = await apiClient.patch<RawGroup>(
      `${API_ENDPOINTS.GROUPS}${groupId}/reorder`,
      {
        item_order: itemOrder,
      },
    );
    invalidateGroupCaches();
    return normalizeGroup(reorderedGroup);
  }

  /**
   * Get groups by workspace
   */
  async getGroupsByWorkspace(workspaceId: number): Promise<NormalizedGroup[]> {
    const groups = await apiClient.get<RawGroup[]>(
      `${API_ENDPOINTS.WORKSPACES}${workspaceId}/groups`,
    );
    return groups.map(normalizeGroup);
  }

  /**
   * Link a group to a workspace
   */
  async linkGroupToWorkspace(
    workspaceId: number,
    groupId: number,
  ): Promise<GroupWorkspaceLink> {
    await apiClient.post(
      `${API_ENDPOINTS.WORKSPACES}${workspaceId}/groups/${groupId}`,
    );
    invalidateGroupCaches();
    return { workspaceId, groupId };
  }

  /**
   * Unlink a group from a workspace
   */
  async unlinkGroupFromWorkspace(
    workspaceId: number,
    groupId: number,
  ): Promise<GroupWorkspaceLink> {
    await apiClient.delete(
      `${API_ENDPOINTS.WORKSPACES}${workspaceId}/groups/${groupId}`,
    );
    invalidateGroupCaches();
    return { workspaceId, groupId };
  }
}

// Create singleton instance
export const groupService = new GroupService();
